import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import useFormOffer from '@features/offers/hooks/useFormOffer';
import {
  FormRetryDiscount,
  OfferFormData,
} from '@features/offers/interfaces/offersInterface';
import { Controller, RadioController, SwitchController } from '@organisms';
import { Button, Col, Form, Radio, Row, Table } from 'antd';
import { useEffect, useMemo } from 'react';
import {
  Control,
  Controller as HookFormController,
  useFieldArray,
} from 'react-hook-form';
import { v4 as uuid } from 'uuid';

import { CurrencyController } from 'src/app/framework/organisms/Controllers/CurrencyController.organism';

export interface FormRetryDiscountProps {
  control: Control<OfferFormData>;
  product: ReturnType<typeof useFormOffer>['product'];
  formType: ReturnType<typeof useFormOffer>['formType'];
  handleRetryDiscounts: ReturnType<
    typeof useFormOffer
  >['handleUpdateRetryDiscountValue'];
}
export const RetryDiscountTable = ({
  control,
  product,
  formType,
  handleRetryDiscounts,
}: FormRetryDiscountProps) => {
  const { append, remove, fields } = useFieldArray<
    OfferFormData,
    'sensitiveData.retryDiscounts'
  >({
    control,
    name: 'sensitiveData.retryDiscounts',
  });

  const defaultIsDirectFallback = useMemo(() => {
    if (!product) return false;
    if (product.subscriptionType === 'monthly') return false;
    if (product.isDirectFallback) return true;
    return false;
  }, [product]);

  useEffect(() => {
    if (fields.length === 0) {
      append({
        key: uuid(),
        percentage: '',
        value: '',
        isDirectFallback: defaultIsDirectFallback,
        active: true,
        typeDiscount: 'value',
      });
    }
  }, []);

  return (
    <Table
      bordered
      size={'small'}
      key={fields.length}
      style={{ width: '100%' }}
      columns={[
        {
          title: 'Valor ativo?',
          dataIndex: 'active',
          key: 'active',
          width: '10%',
          align: 'center',
          render: (enabled: boolean, record: FormRetryDiscount, index) => (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SwitchController
                control={control}
                defaultValue={enabled}
                name={`sensitiveData.retryDiscounts.${index}.active`}
              />
            </div>
          ),
        },
        {
          title: () => <Row justify={'center'}>Valor</Row>,
          dataIndex: 'typeDiscount',
          key: 'typeDiscount',
          render: (
            typeDiscount: 'percentage' | 'value',
            record: FormRetryDiscount,
            index
          ) => {
            return (
              <HookFormController
                name={`sensitiveData.retryDiscounts.${index}.typeDiscount`}
                control={control}
                defaultValue={typeDiscount}
                render={({ field }) => (
                  <Form.Item rules={[{ required: true }]}>
                    <Radio.Group
                      style={{
                        padding: 8,
                        width: '100%',
                        display: 'flex',
                        gap: 8,
                      }}
                      {...field}
                    >
                      <div
                        style={{
                          flex: 1,
                          gap: 8,
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Radio value='value'>Valor com desconto</Radio>
                        <CurrencyController
                          control={control}
                          name={`sensitiveData.retryDiscounts.${index}.value`}
                          required
                          disabled={
                            formType === 'view' || field.value === 'percentage'
                          }
                          onValueChange={e =>
                            handleRetryDiscounts(index, 'value', e)
                          }
                          reserveErrorSpace
                        />
                      </div>
                      <div
                        style={{
                          flex: 1,
                          gap: 8,
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Radio value='percentage'>
                          Percentual de desconto{' '}
                        </Radio>
                        <Controller
                          control={control}
                          name={`sensitiveData.retryDiscounts.${index}.percentage`}
                          suffix='%'
                          type='number'
                          required
                          disabled={
                            formType === 'view' || field.value === 'value'
                          }
                          onValueChange={e =>
                            handleRetryDiscounts(index, 'percentage', e)
                          }
                        />
                      </div>
                    </Radio.Group>
                  </Form.Item>
                )}
              />
            );
          },
        },
        {
          title: 'Full e Fallback?',
          dataIndex: 'isDirectFallback',
          key: 'isDirectFallback',
          width: '25%',
          align: 'center',
          render: (isDirectFallback: boolean, _, index) => (
            <RadioController
              control={control}
              name={`sensitiveData.retryDiscounts.${index}.isDirectFallback`}
              value={isDirectFallback}
              options={[
                { label: 'Full e fallback', value: false },
                { label: 'Apenas fallback', value: true },
              ]}
              disabled={
                formType === 'view' ||
                product?.subscriptionType === 'monthly' ||
                product?.isDirectFallback
              }
            />
          ),
        },
        {
          title: 'Ações',
          dataIndex: 'actions',
          key: 'actions',
          width: '5%',
          align: 'center',
          render: (_, record, index) => (
            <Button
              type='text'
              danger
              onClick={() => {
                remove(index);
              }}
              icon={<DeleteOutlined />}
            />
          ),
        },
      ]}
      dataSource={fields}
      pagination={false}
      footer={() => (
        <Row justify={'space-between'} style={{ padding: 4 }}>
          <Col style={{ alignContent: 'center' }}>
            {fields.filter(field => field.active).length}/10
          </Col>
          <Col>
            <Button
              type='primary'
              disabled={formType === 'view' || fields.length > 9}
              onClick={() => {
                append({
                  key: uuid(),
                  percentage: '',
                  value: '',
                  isDirectFallback: defaultIsDirectFallback,
                  active: true,
                  typeDiscount: 'value',
                });
              }}
              size='middle'
              icon={<PlusOutlined />}
            >
              Adicionar
            </Button>
          </Col>
        </Row>
      )}
    />
  );
};
